import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import CaleoSecondaryButton from 'Components/reusable/Buttons/CaleoSecondaryButton'
import { useUser } from 'hooks'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { clearWizardDrafts, getWizardDraftSteps, loadWizardLastStep } from './wizardDraft'
import { getWizardPath } from './wizardSteps'

/** @notExported */
interface IWizardResumePromptProps {
  /** ID of the profile's person. */
  personId: number
  /** ID of the selected CV. */
  cvId: number
}

/**
 * Asks whether to continue the CV wizard when the user has unsaved wizard drafts for the CV.
 * Continue opens the step the user was last on, Discard removes the drafts and Later closes the
 * prompt until the next visit.
 *
 * @returns Resume prompt, or nothing when there are no drafts.
 * @notExported
 */
const WizardResumePrompt: React.FC<IWizardResumePromptProps> = ({ personId, cvId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useUser()
  const userId = user?.id
  const [closedCvIds, setClosedCvIds] = useState<number[]>([])

  const resumeStep = useMemo(() => {
    if (!userId) return undefined
    const draftSteps = getWizardDraftSteps(userId, cvId)
    if (!draftSteps.length) return undefined
    return loadWizardLastStep(userId, cvId) ?? draftSteps[0]
  }, [userId, cvId])

  if (!userId || !resumeStep || closedCvIds.includes(cvId)) {
    return null
  }

  const close = () => setClosedCvIds(previous => [...previous, cvId])

  const discard = () => {
    clearWizardDrafts(userId, cvId)
    close()
  }

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth aria-labelledby="wizard-resume-title">
      <DialogTitle id="wizard-resume-title">{t('profile.wizard.resume.title')}</DialogTitle>
      <DialogContent>
        <Typography>{t('profile.wizard.resume.text')}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CaleoSecondaryButton
          testId="button-wizard-resume-later"
          valid={true}
          label={t('profile.wizard.resume.later')}
          clickAction={close}
        />
        <CaleoSecondaryButton
          testId="button-wizard-resume-discard"
          valid={true}
          label={t('profile.wizard.resume.discard')}
          clickAction={discard}
        />
        <CaleoPrimaryButton
          testId="button-wizard-resume-continue"
          valid={true}
          label={t('profile.wizard.resume.continue')}
          clickAction={() => navigate(getWizardPath(personId, cvId, resumeStep))}
        />
      </DialogActions>
    </Dialog>
  )
}

export default WizardResumePrompt
